import { ref } from 'vue';
import { defineStore } from 'pinia';
import { secure, StorageKeys } from '@/core/storage/secure';
import type { PrinterDevice, ReceiptLayout } from '@/core/models';

export const usePrinterStore = defineStore('printer', () => {
  // The paired printer is a device-local pairing (persisted to Preferences so it
  // survives reloads), not tenant data. The receipt layout is fetched from the
  // backend on demand, so it stays in-memory only.
  const device = ref<PrinterDevice | null>(null);
  const layout = ref<ReceiptLayout | null>(null);
  const hydrated = ref(false);

  /** Restore the paired device from local storage. Safe to call repeatedly. */
  async function hydrate(): Promise<void> {
    if (hydrated.value) return;
    device.value = await secure.getJson<PrinterDevice>(StorageKeys.PrinterDevice);
    hydrated.value = true;
  }

  function setDevice(d: PrinterDevice | null): void {
    device.value = d;
    // Write-through so the pairing survives a reload.
    if (d) void secure.setJson(StorageKeys.PrinterDevice, d);
    else void secure.remove(StorageKeys.PrinterDevice);
  }

  function setLayout(l: ReceiptLayout | null): void {
    layout.value = l;
  }

  function clear(): void {
    device.value = null;
    layout.value = null;
  }

  return { device, layout, hydrated, hydrate, setDevice, setLayout, clear };
});
